import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";

import type { UserContextState } from "./UserContext";
import type { SessionDto } from "Src/api/Dto";

import { UserType } from "Src/api/Enums";
import HttpClient from "Src/api/HttpClient";
import { getSession } from "Src/api/Session";

import { useError } from "Src/components/Common/ErrorBoundary";

import { loadAll } from "Src/squawk/ActionsApp";
import { appInsights } from "Src/utils/AppInsights";

import { parseQuery } from "../../utils/QueryParser";
import CustomAlert from "../Common/CustomAlert";

import { defaultUserContextState, UserContext } from "./UserContext";

interface Props {
  children?: React.ReactNode;
}

const defaultLoginUrl = `${import.meta.env.BOKINFO_LOGIN_URL}/sv-SE/logga-in/`;

export function UserProvider({ children }: Props) {
  const [state, setState] = useState<UserContextState>(defaultUserContextState);
  const [loading, setLoading] = useState(true);
  const [authorizationError, setAuthorizationError] = useState("");
  const location = useLocation();
  const dispatchError = useError();
  const history = useHistory();

  const { unauthorized } = state;

  useEffect(() => {
    HttpClient.withCredentials(true);

    void load();
  }, []);

  const load = async () => {
    // TODO: Add check of path so we only parse Key if we're at the root url
    const queryString = parseQuery(location.search);

    let user: SessionDto;
    let redirect = false;
    setLoading(true);
    try {
      // if (epiKey === "" && userStringified && epiKeyStorage && epiKeyStorage !== "null" && userStringified !== "null") {
      //   // Handles case when an authenticated user has reloaded the page by getting and setting data from session storage
      //   user = JSON.parse(userStringified) as SessionDto;
      //   redirect = location.pathname === "/";
      // } else {
      user = await getSession(undefined, undefined, true);
      localStorage.setItem("user", JSON.stringify(user));
      redirect = location.pathname === "/";
      // }

      const apiKey = user.subscriptions.find((subscription) => subscription.key === "EbookApi")?.value ?? "";

      setState((previousState) => ({ ...previousState, ...user, unauthorized: false, apiKey }));

      // user = { ...user, brId: user.brId.trim() };
      setState({ ...state, ...user, unauthorized: false, apiKey: user.subscriptions[0]?.value });
      HttpClient.setDefaultHeaders({ "Ocp-Apim-Subscription-Key": apiKey });

      // In test and production we use api management. Api management takes care of adding brId as a header
      if (import.meta.env.NODE_ENV === "development") {
        HttpClient.setDefaultHeaders({ brId: user.brId });
      }

      if (user.type === UserType.Publisher) {
        await loadAll(user.brId);
      }

      appInsights.addTelemetryInitializer((envelope) => {
        const telemetryItem = envelope.baseData;
        if (telemetryItem) {
          telemetryItem.properties["ai.user.brId"] = user.brId;
          telemetryItem.properties["ai.user.type"] = UserType[user.type];
        }

        return true;
      });

      setLoading(false);
      if (redirect) {
        if (user.type === UserType.Admin) {
          history.replace("/admin/publisherbooks");
        } else {
          const isbn = queryString.get("isbn") as string;
          history.replace(isbn ? `/publisher/upload/${isbn}` : "/publisher/books/book-list");
        }
      }
    } catch (error) {
      if (error instanceof XMLHttpRequest && error.status === 401) {
        const loginUrl = error.getResponseHeader("X-Login-Url") || defaultLoginUrl;
        window.location.assign(`${loginUrl}?returnUrl=${encodeURIComponent(window.location.href)}`);

        return;
      }

      if (error instanceof XMLHttpRequest && error.status === 403) {
        // Authenticated but lacks digital access; redirecting to login would loop
        setAuthorizationError(error.responseText?.trim() || `HTTP ${error.status}`);
        setState((previousState) => ({ ...previousState, unauthorized: true }));
        setLoading(false);

        return;
      }

      setLoading(false);
      dispatchError(error);
    }
  };

  if (loading) {
    return (
      <div className="h-100 d-flex justify-content-center align-items-center">
        <Spinner animation="border" />
      </div>
    );
  } else if (unauthorized) {
    return (
      <CustomAlert
        title="Åtkomst kunde inte bekräftas"
        content={
          <>
            <p>Vi kunde inte bekräfta din åtkomst till Bokinfo Digital.</p>
            <p className="mb-0">
              Felmeddelande (HTTP 403): {authorizationError || "Okänt fel"}
            </p>
          </>
        }
      />
    );
  }

  return <UserContext.Provider value={state}>{children}</UserContext.Provider>;
}
